import { useCallback, useState } from "react";
import { Flex, Text, Button, Badge, TextField } from "@radix-ui/themes";
import { MixerHorizontalIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  ReleaseAccessStatus,
  accessStatusToLabel,
  accessStatusOptions,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.model";

export interface ResourcesFilterState {
  accessLevels: ReleaseAccessStatus[];
  searchQuery: string;
  categories: string[];
}

interface ResourcesToolbarProps {
  title: string;
  count: number;
  filterState: ResourcesFilterState;
  onFilterChange: (filter: ResourcesFilterState) => void;
  selectedCount: number;
  onBulkAccessClick: () => void;
  categoryFilter: {
    title: string;
    options: string[];
  }
}

export function ResourcesToolbar({
  title,
  count,
  filterState,
  onFilterChange,
  selectedCount,
  onBulkAccessClick,
  categoryFilter,
}: ResourcesToolbarProps) {
  const { title: categoryFilterTitle, options: categoryFilterOptions } = categoryFilter;
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleAccessFilterChange = useCallback(
    (status: ReleaseAccessStatus, checked: boolean) => {
      const newAccessLevels = checked
        ? [...filterState.accessLevels, status]
        : filterState.accessLevels.filter((s) => s !== status);
      onFilterChange({ ...filterState, accessLevels: newAccessLevels });
    },
    [filterState, onFilterChange]
  );

  const handleCategoryFilterChange = useCallback(
    (category: string, checked: boolean) => {
      const newCategories = checked
        ? [...filterState.categories, category]
        : filterState.categories.filter((c) => c !== category);
      onFilterChange({ ...filterState, categories: newCategories });
    },
    [filterState, onFilterChange]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ ...filterState, searchQuery: e.target.value });
    },
    [filterState, onFilterChange]
  );

  const hasActiveFilters = filterState.accessLevels.length > 0;

  return (
    <Flex justify="between" align="center" py="3">
      <Flex align="center" gap="3">
        <Flex align="center" gap="2">
          <Text size="3" weight="medium">
            {title}
          </Text>
          <Badge color="gray" size="1" variant="soft">
            {count.toLocaleString()}
          </Badge>
        </Flex>

        <Flex align="center" gap="2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="surface" color="gray" highContrast size="1">
                <MixerHorizontalIcon />
                Filter
                {hasActiveFilters && (
                  <Badge color="blue" size="1" variant="soft">
                    {filterState.accessLevels.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Access Level</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {accessStatusOptions.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={filterState.accessLevels.includes(status)}
                  onCheckedChange={(checked: boolean) =>
                    handleAccessFilterChange(status, checked)
                  }
                >
                  {accessStatusToLabel[status]}
                </DropdownMenuCheckboxItem>
              ))}
              {categoryFilterOptions.length > 0 && (
                <>
                  <DropdownMenuLabel>{categoryFilterTitle}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {categoryFilterOptions.map((category) => (
                    <DropdownMenuCheckboxItem
                      key={category}
                      checked={filterState.categories.includes(category)}
                      onCheckedChange={(checked: boolean) =>
                        handleCategoryFilterChange(category, checked)
                      }
                    >
                      {category}
                    </DropdownMenuCheckboxItem>
                  ))}
              </>)}
            </DropdownMenuContent>
          </DropdownMenu>

          {isSearchOpen ? (
            <TextField.Root
              size="1"
              placeholder="Search..."
              value={filterState.searchQuery}
              onChange={handleSearchChange}
              autoFocus
              onBlur={() => {
                if (filterState.searchQuery === "") {
                  setIsSearchOpen(false);
                }
              }}
            >
              <TextField.Slot>
                <MagnifyingGlassIcon height="14" width="14" />
              </TextField.Slot>
            </TextField.Root>
          ) : (
            <Button
              variant="surface"
              color="gray"
              highContrast
              size="1"
              onClick={() => setIsSearchOpen(true)}
            >
              <MagnifyingGlassIcon />
              Find
            </Button>
          )}
        </Flex>
      </Flex>

      {selectedCount > 0 && (
        <Flex align="center" gap="3">
          <Text size="2" color="gray">
            {selectedCount} selected
          </Text>
          <Button
            variant="soft"
            color="gray"
            highContrast
            size="1"
            onClick={onBulkAccessClick}
          >
            Change Access
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
